import { Profiler } from 'react';
import type { ProfilerOnRenderCallback, ReactNode } from 'react';
import { PROFILING_BUILD } from 'config';

/**
 * Render-profiling wrapper around React's <Profiler>.
 *
 * The `onRender` callback only does real work when profiling is enabled
 * (`VITE_REACT_PROFILING=true`, which also aliases `react-dom` → its profiling
 * bundle in vite.config so commits are timed in a production-shaped build). In a
 * normal production/dev build this is a pass-through: no <Profiler>, no overhead, and
 * no global state.
 *
 * Samples are pushed onto `window.__RENDER_PROFILE__` so a Playwright spec can read
 * them with `page.evaluate(() => window.__RENDER_PROFILE__)`. Reference spec:
 * `e2e/src/pages/styles/styles-profiling.spec.ts`.
 */

export type RenderSample = {
  /** The `id` prop of the enclosing <RenderProfiler>. */
  id: string;
  /** 'mount' on first render, 'update' on re-render, 'nested-update' when triggered during commit. */
  phase: 'mount' | 'update' | 'nested-update';
  /** Time spent rendering this commit (ms) — the headline number. */
  actualDuration: number;
  /** Estimated render time for the whole subtree without memoization (ms). */
  baseDuration: number;
  startTime: number;
  commitTime: number;
};

declare global {
  interface Window {
    __RENDER_PROFILE__?: RenderSample[];
  }
}

const onRender: ProfilerOnRenderCallback = (
  id,
  phase,
  actualDuration,
  baseDuration,
  startTime,
  commitTime
) => {
  (window.__RENDER_PROFILE__ ??= []).push({
    id,
    phase,
    actualDuration,
    baseDuration,
    startTime,
    commitTime,
  });
};

type RenderProfilerProps = {
  id: string;
  children: ReactNode;
};

export const RenderProfiler = ({ id, children }: RenderProfilerProps) => {
  if (!PROFILING_BUILD) return children;

  return (
    <Profiler id={id} onRender={onRender}>
      {children}
    </Profiler>
  );
};
